import type { LayerLibraryRow, ScopeTabOption, SubLayerType } from '@civilgrid/ui/components';
import type { AvailableLayer } from '~/api/availableLayers';
import type { MapboxSourceConfig, TilesConfig } from '~/store2/layers.data';
import { deriveLayerColors, deriveLayerKind, deriveSubLayerType } from './layerAssets';

// Pure helpers for the Layer Library page. No React / no I/O — unit-testable.

/**
 * Scope tabs for the Layer Library — each selects which catalog table renders in the
 * center. The tab values mirror `AvailableLayer.scope` 1:1, so the tab IS the scope
 * filter (see {@link filterLayersForTab}). `special` shows the org's entitled
 * special-dataset views — org-custom uploads are special datasets, so they appear here too.
 */
export const LAYER_LIBRARY_TABS = ['civilgrid', 'special'] as const;
export type LayerLibraryTab = (typeof LAYER_LIBRARY_TABS)[number];

/** Default tab when none (or an unknown one) is in the URL. */
export const DEFAULT_LAYER_LIBRARY_TAB: LayerLibraryTab = 'civilgrid';

/** Normalize a raw `$tabId` path param to a known scope tab; unknown/missing → default. */
export function resolveLayerLibraryTab(raw: string | undefined): LayerLibraryTab {
  return LAYER_LIBRARY_TABS.includes(raw as LayerLibraryTab) ? (raw as LayerLibraryTab) : DEFAULT_LAYER_LIBRARY_TAB;
}

/** `ScopeTabs` options (labels per the design system — CivilGrid Layers / Special Datasets). */
export const LAYER_LIBRARY_TAB_OPTIONS: ScopeTabOption[] = [
  { value: 'civilgrid', label: 'CivilGrid Layers' },
  { value: 'special', label: 'Special Datasets' },
];

/**
 * `ScopeTabs` options with live counts — the number of Library layers (group grain)
 * each scope shows, rendered by the ui `Badge`. Counts come from the same
 * {@link filterLayersForTab} + {@link unselectedLayers} pipeline that fills the table
 * (already-added layers leave the catalog), so badge and table always agree.
 */
export function layerLibraryTabOptions(
  layers: readonly AvailableLayer[],
  libraryLayerIds: ReadonlySet<string>,
): ScopeTabOption[] {
  return LAYER_LIBRARY_TAB_OPTIONS.map((option) => ({
    ...option,
    count: unselectedLayers(filterLayersForTab(option.value as LayerLibraryTab, layers), libraryLayerIds).length,
  }));
}

export type LayerLibraryAccess = 'redirect' | 'allow';

/**
 * Resolve page access from verification status. Unverified users are 'redirect' — the Library
 * exists to browse/add utility and special-dataset layers, all of which are blocked for them, so
 * the page must not be reachable (its TOC entry points hide too, see `LayerLibraryEntry`).
 */
export function resolveLayerLibraryAccess(inUnverifiedMode: boolean): LayerLibraryAccess {
  return inUnverifiedMode ? 'redirect' : 'allow';
}

/** Default sub-layer icon type while a leaf's tiles-config entry hasn't loaded. */
const DEFAULT_SUB_LAYER_TYPE = 'line' as const;

/** Point-like geometry reads poorly as a thumb/preview — it triggers the Lines > Points scan. */
const POINT_LIKE_TYPES: ReadonlySet<SubLayerType> = new Set<SubLayerType>(['point', 'circle']);
/** Geometry preferred for thumbs/previews when the first sub-layer is point-like. */
const PREFERRED_TYPES: ReadonlySet<SubLayerType> = new Set<SubLayerType>(['line', 'polygon']);

/**
 * The tiles-config entry that represents a Library row — it drives both the
 * Name-column icon and the popover's live-map preview. The **first sub-layer's**
 * entry, except when that entry draws point-like geometry (symbol points / circles):
 * lines and polygons read better as thumbs (Figma #21), so the remaining sub-layers
 * are scanned in order and the first line/polygon entry wins. An all-points group
 * keeps its first entry, as do rows whose first entry is raster or not yet loaded.
 * Leaf-grain rows (org-custom datasets without sub-layers) fall back to the row's
 * own key.
 */
export function rowTilesConfigEntry(
  layer: AvailableLayer,
  tilesConfig: TilesConfig | undefined,
): MapboxSourceConfig | undefined {
  if (!tilesConfig) return undefined;
  const first = tilesConfig[layer.subLayers[0]?.key ?? layer.key];
  const firstType = deriveSubLayerType(first);
  if (!firstType || !POINT_LIKE_TYPES.has(firstType)) return first;
  for (const subLayer of layer.subLayers.slice(1)) {
    const entry = tilesConfig[subLayer.key];
    const type = deriveSubLayerType(entry);
    if (type && PREFERRED_TYPES.has(type)) return entry;
  }
  return first;
}

/**
 * Map a catalog `AvailableLayer` (group grain) to the shared table's
 * presentational `LayerLibraryRow`. `@civilgrid/ui` has no dependency on the
 * catalog source, so this page owns the translation. Icon kind/color and sub-layer
 * geometry types are derived from the session-cached tiles config — pass the
 * Map's `layerStore` config dict (base + custom merged); while it hasn't loaded, rows
 * fall back to the default icons.
 */
export function toLayerLibraryRow(layer: AvailableLayer, tilesConfig?: TilesConfig): LayerLibraryRow {
  const entry = rowTilesConfigEntry(layer, tilesConfig);
  // The document glyph (custom datasets) keeps its fixed color — paint colors apply
  // only to geometry kinds (raster already yields none). Fill/outline pass verbatim,
  // transparency included — the glyph draws what the map draws.
  const colors = layer.customDataset ? {} : deriveLayerColors(entry);
  return {
    id: layer.key,
    name: layer.label,
    layerKind: deriveLayerKind(entry, { customDataset: layer.customDataset }),
    layerKindColor: colors.fill,
    layerKindOutlineColor: colors.outline,
    category: layer.category,
    // description/source/timestamps are omitted (left undefined) when the catalog has none — the
    // table renders a blank cell and the popover hides the field (no em-dash placeholder).
    source: layer.source,
    updated: layer.updated ? new Date(layer.updated) : undefined,
    dateCreated: layer.created ? new Date(layer.created) : undefined,
    description: layer.description,
    subLayers: layer.subLayers.map((subLayer) => ({
      name: subLayer.label,
      type: deriveSubLayerType(tilesConfig?.[subLayer.key]) ?? DEFAULT_SUB_LAYER_TYPE,
    })),
  };
}

/**
 * The catalog slice a scope tab shows: the tab value is the `scope` discriminator, so this is a
 * straight `layer.scope === tab` filter — `civilgrid` (CivilGrid-defined, global or org-private) or
 * `special` (entitled special-dataset views). Rows stay group-grain (`AvailableLayer`).
 * Filtering on `scope` (not `customDataset`) is what keeps special datasets in their own tab — many
 * carry `customDataset: true`, while org-private CivilGrid layers carry `customDataset: false`.
 */
export function filterLayersForTab(tab: LayerLibraryTab, layers: readonly AvailableLayer[]): AvailableLayer[] {
  return layers.filter((layer) => layer.scope === tab);
}

/** The user's selected (in-library) layers, in catalog order — the Selected Layers table. */
export function selectedLayers(
  layers: readonly AvailableLayer[],
  libraryLayerIds: ReadonlySet<string>,
): AvailableLayer[] {
  return layers.filter((layer) => libraryLayerIds.has(layer.key));
}

/**
 * Catalog layers still available to add — the complement of {@link selectedLayers}.
 * Added layers move to the Selected table instead of rendering an "Added" state in
 * the catalog, so the catalog table shows only these.
 */
export function unselectedLayers(
  layers: readonly AvailableLayer[],
  libraryLayerIds: ReadonlySet<string>,
): AvailableLayer[] {
  return layers.filter((layer) => !libraryLayerIds.has(layer.key));
}

/**
 * The Site Data TOC section node id — the group key its leaves hang off. Matches on
 * identity, not on the leaf set: the server's membership guarantee keys off this exact
 * bucket key, so anything narrower (e.g. "are all of this group's sub-layers known Site
 * Data ids?") would disagree the moment a leaf is added or removed under the section, and
 * the group would reappear in the Library with Add/Remove buttons the server overrides on
 * the next load. Duplicated client-side rather than imported from `@civilgrid/utils`
 * (`SITE_DATA_TOC_PARENT_KEY`), the same duplication-by-design as `DIGITIZED_SECTION_KEY`
 * and the `api/availableLayers.ts` wire types.
 */
const SITE_DATA_SECTION_KEY = '_toc:Site Data';

/** True for the Library's "Site Data" catalog group — the server's Site Data section node. */
function isSiteDataGroup(layer: AvailableLayer): boolean {
  return layer.key === SITE_DATA_SECTION_KEY;
}

/**
 * Drop the Site Data group from the Library display catalog, unconditionally. Site Data
 * is not library-curated: its membership is guaranteed server-side (the load-path
 * reconciliation always injects its leaves), so there is nothing to Add or Remove — the
 * group must never appear in the catalog table, the Selected panel, or the tab counts,
 * on either the user or the org Library. Display filter only — the underlying
 * catalog/document membership is untouched, and the picker still renders the section
 * (gated per-project by data availability, `isSiteDataLeafEmpty`).
 */
export function filterSiteDataFromCatalog(layers: readonly AvailableLayer[]): AvailableLayer[] {
  return layers.filter((layer) => !isSiteDataGroup(layer));
}

export type ResetDialogArgs = { scope: 'org' } | { scope: 'user'; hasOrgDefaults: boolean };
export interface ResetDialogCopy {
  title: string;
  body: string;
  cancel: string;
  confirm: string;
}

/**
 * Copy for the Layer Library reset-confirmation dialog (CG-725, Figma node 658-77625). The user
 * dialog names the actual fallback (org vs CivilGrid); the org dialog always names CivilGrid.
 * Pure — unit-testable, no React/I-O.
 */
export function resetDialogCopy(args: ResetDialogArgs): ResetDialogCopy {
  const title = 'Are you sure you want to reset your layers?';
  const cancel = 'Back';
  const confirm = 'Yes, Reset Layers';
  if (args.scope === 'org') {
    return {
      title,
      cancel,
      confirm,
      body: `Applying this action will reset your organization's default layers to the CivilGrid default layers, and any layers you've added will return to the library. You can re-add these layers at any time.`,
    };
  }
  const target = args.hasOrgDefaults ? `your organization's default layers` : `the CivilGrid default layers`;
  return {
    title,
    cancel,
    confirm,
    body: `Applying this action will reset your layers to ${target}, and any layers you've added will return to the library. You can re-add these layers at any time.`,
  };
}
